// Live Booking Activity Feed Service

export type ActivityEventType =
  | 'booking_created' |'booking_confirmed' |'booking_cancelled' |'payment_received' |'ticket_issued' |'check_in_opened' |'visa_approved' |'support_ticket_created' |'support_ticket_resolved' |'price_drop' |'new_lead' |'flight_search';

export interface ActivityEvent {
  id: string;
  type: ActivityEventType;
  title: string;
  description: string;
  destination?: string;
  amount?: number;
  bookingRef?: string;
  userName?: string;
  timestamp: string;
  icon: string;
  color: string;
  isNew?: boolean;
}

const ACTIVITY_KEY = 'dff_activity_feed';
const MAX_EVENTS = 50;

const EVENT_TEMPLATES: Record<ActivityEventType, { icon: string; color: string; titles: string[] }> = {
  booking_created: { icon: '✈️', color: 'blue', titles: ['New booking started', 'Flight booking initiated', 'Holiday package selected'] },
  booking_confirmed: { icon: '✅', color: 'green', titles: ['Booking confirmed!', 'Reservation secured', 'Tickets confirmed'] },
  booking_cancelled: { icon: '❌', color: 'red', titles: ['Booking cancelled', 'Reservation cancelled'] },
  payment_received: { icon: '💷', color: 'green', titles: ['Payment received', 'Payment processed', 'Transaction complete'] },
  ticket_issued: { icon: '🎫', color: 'purple', titles: ['E-ticket issued', 'Boarding pass ready', 'Ticket generated'] },
  check_in_opened: { icon: '🛫', color: 'blue', titles: ['Check-in now open', 'Online check-in available'] },
  visa_approved: { icon: '🛂', color: 'green', titles: ['Visa approved', 'Visa application successful'] },
  support_ticket_created: { icon: '🎫', color: 'orange', titles: ['Support ticket opened', 'Customer query received', 'Help request submitted'] },
  support_ticket_resolved: { icon: '✔️', color: 'green', titles: ['Support ticket resolved', 'Query answered', 'Issue resolved'] },
  price_drop: { icon: '📉', color: 'yellow', titles: ['Price drop alert!', 'Fare reduced', 'Special deal available'] },
  new_lead: { icon: '👤', color: 'indigo', titles: ['New enquiry received', 'Lead captured', 'Customer interested'] },
  flight_search: { icon: '🔍', color: 'gray', titles: ['Flight search performed', 'Routes searched', 'Availability checked'] },
};

const DESTINATIONS = ['Dubai', 'New York', 'Lahore', 'Karachi', 'Islamabad', 'Toronto', 'Sydney', 'Bangkok', 'Istanbul', 'Doha', 'Nairobi', 'Lagos', 'Accra', 'Dhaka', 'Mumbai', 'Delhi', 'Colombo', 'Kuala Lumpur', 'Singapore', 'Cape Town'];
const NAMES = ['Ahmed K.', 'Sarah M.', 'James T.', 'Fatima A.', 'David L.', 'Priya S.', 'Mohammed R.', 'Emma W.', 'Yusuf B.', 'Aisha N.', 'Chris P.', 'Zara H.'];

function generateBookingRef(): string {
  return 'DFF' + Math.random().toString(36).substr(2, 6).toUpperCase();
}

function generateAmount(type: ActivityEventType): number | undefined {
  if (['booking_confirmed', 'payment_received', 'price_drop'].includes(type)) {
    return Math.floor(Math.random() * 1200) + 200;
  }
  return undefined;
}

export function generateActivityEvent(type?: ActivityEventType): ActivityEvent {
  const eventType = type || (Object.keys(EVENT_TEMPLATES) as ActivityEventType[])[Math.floor(Math.random() * Object.keys(EVENT_TEMPLATES).length)];
  const template = EVENT_TEMPLATES[eventType];
  const title = template.titles[Math.floor(Math.random() * template.titles.length)];
  const destination = DESTINATIONS[Math.floor(Math.random() * DESTINATIONS.length)];
  const userName = NAMES[Math.floor(Math.random() * NAMES.length)];
  const amount = generateAmount(eventType);

  let description = '';
  switch (eventType) {
    case 'booking_created': description = `${userName} started booking to ${destination}`; break;
    case 'booking_confirmed': description = `${userName}'s trip to ${destination} confirmed${amount ? ` — £${amount}` : ''}`; break;
    case 'payment_received': description = `Payment of £${amount} received for ${destination} booking`; break;
    case 'ticket_issued': description = `E-ticket issued for ${userName} to ${destination}`; break;
    case 'support_ticket_created': description = `${userName} opened a support request`; break;
    case 'support_ticket_resolved': description = `Support query from ${userName} resolved`; break;
    case 'price_drop': description = `${destination} flights from £${amount} — limited time`; break;
    case 'new_lead': description = `${userName} enquired about ${destination}`; break;
    case 'flight_search': description = `${userName} searched flights to ${destination}`; break;
    default: description = `Activity for ${destination}`;
  }

  return {
    id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    type: eventType,
    title,
    description,
    destination,
    amount,
    bookingRef: ['booking_confirmed', 'payment_received', 'ticket_issued'].includes(eventType) ? generateBookingRef() : undefined,
    userName,
    timestamp: new Date().toISOString(),
    icon: template.icon,
    color: template.color,
    isNew: true,
  };
}

export function addActivityEvent(event: ActivityEvent): void {
  if (typeof window === 'undefined') return;
  const events = getActivityEvents();
  events.unshift(event);
  localStorage.setItem(ACTIVITY_KEY, JSON.stringify(events.slice(0, MAX_EVENTS)));
}

export function getActivityEvents(): ActivityEvent[] {
  if (typeof window === 'undefined') return [];
  return JSON.parse(localStorage.getItem(ACTIVITY_KEY) || '[]');
}

export function seedInitialActivity(): void {
  if (typeof window === 'undefined') return;
  const existing = getActivityEvents();
  if (existing.length > 0) return;
  const types: ActivityEventType[] = ['booking_confirmed', 'payment_received', 'ticket_issued', 'new_lead', 'flight_search', 'booking_created', 'support_ticket_created', 'price_drop', 'booking_confirmed', 'payment_received'];
  const events: ActivityEvent[] = types.map((type, i) => {
    const evt = generateActivityEvent(type);
    const ts = new Date(Date.now() - i * 4 * 60 * 1000);
    return { ...evt, timestamp: ts.toISOString(), isNew: false };
  });
  localStorage.setItem(ACTIVITY_KEY, JSON.stringify(events));
}
